/**
 * Club permissions — source of truth for flying-club organizations.
 *
 * Clubs have two roles: `admin` and `member`. Club operations are rental-based;
 * there is no training, no student/instructor assignments, no availability
 * scheduling, and no dispatch center — so those permission groups
 * (ASSIGNMENTS / TRAINING / AVAILABILITY) are intentionally absent here, even
 * for admins. The cross-vertical resolver (features/shared/permissions)
 * dispatches to this table for club organizations.
 */
import { Permission } from '../../../core/permissions';

export const CLUB_ROLES = ['admin', 'member', 'maintenance'] as const;
export type ClubRole = typeof CLUB_ROLES[number];

export const CLUB_ROLE_PERMISSIONS: Record<ClubRole, Permission[]> = {
  admin: [
    // Full club administration — everything except the school-only groups
    // (ASSIGNMENTS / TRAINING / AVAILABILITY), which are not reachable in a club.
    'BOOKINGS:CREATE', 'BOOKINGS:READ', 'BOOKINGS:UPDATE', 'BOOKINGS:DELETE', 'BOOKINGS:CANCEL_OTHERS', 'BOOKINGS:DISPATCH_OVERRIDE',
    'AIRCRAFT:CREATE', 'AIRCRAFT:READ', 'AIRCRAFT:UPDATE', 'AIRCRAFT:DELETE',
    'MEMBERS:CREATE', 'MEMBERS:READ', 'MEMBERS:UPDATE', 'MEMBERS:DELETE', 'MEMBERS:ASSIGN_ROLES',
    'MAINTENANCE:READ', 'MAINTENANCE:CREATE_SQUAWK', 'MAINTENANCE:UPDATE_SQUAWK', 'MAINTENANCE:CLOSE_SQUAWK', 'MAINTENANCE:APPROVE_WORK',
    'FLIGHT_PLANS:CREATE', 'FLIGHT_PLANS:READ', 'FLIGHT_PLANS:UPDATE', 'FLIGHT_PLANS:DELETE', 'FLIGHT_PLANS:APPROVE', 'FLIGHT_PLANS:DISPATCH_OVERRIDE',
    'ORG:READ', 'ORG:UPDATE', 'ORG:MANAGE_SETTINGS', 'ORG:VIEW_ANALYTICS',
    'DOCUMENTS:READ', 'DOCUMENTS:UPLOAD', 'DOCUMENTS:VERIFY', 'DOCUMENTS:DELETE'
  ],

  member: [
    // Standard pilot permissions - can update aircraft hours when completing flights
    'BOOKINGS:CREATE', 'BOOKINGS:READ', 'BOOKINGS:UPDATE', 'BOOKINGS:DELETE',
    'AIRCRAFT:READ', 'AIRCRAFT:UPDATE_HOURS',
    'MEMBERS:READ', // Can view member list (read-only, no edit)
    'MAINTENANCE:READ', 'MAINTENANCE:CREATE_SQUAWK',
    'FLIGHT_PLANS:CREATE', 'FLIGHT_PLANS:READ', 'FLIGHT_PLANS:UPDATE', 'FLIGHT_PLANS:DELETE',
    'ORG:READ',
    'DOCUMENTS:READ', 'DOCUMENTS:UPLOAD'
  ],

  maintenance: [
    // Club maintenance: aircraft squawks and work orders; can view bookings for coordination.
    // Adjust this list independently of school/maintenance if club rules diverge.
    'BOOKINGS:READ',
    'AIRCRAFT:READ', 'AIRCRAFT:UPDATE',
    'MEMBERS:READ',
    'MAINTENANCE:READ', 'MAINTENANCE:CREATE_SQUAWK', 'MAINTENANCE:UPDATE_SQUAWK', 'MAINTENANCE:CLOSE_SQUAWK', 'MAINTENANCE:APPROVE_WORK',
    'ORG:READ',
    'DOCUMENTS:READ'
  ]
};

/**
 * Check if a user can manage aircraft within a club organization.
 * Only admins manage club aircraft; returns false for non-club organizations.
 */
export function canManageClubAircraft(
  user: { role: string } | null,
  org: { type: string } | null
): boolean {
  if (!user || !org) return false;
  if (org.type !== 'club') return false;
  return user.role === 'admin';
}
